import React, {Fragment, useEffect, useState} from 'react';
import {MainNavbar} from '@app/main-navbar';
import {useDarkThemeVariables} from '@ui/themes/use-dark-theme-variables';
import {Footer} from '@common/ui/footer/footer';
import {CommentList} from '@common/comments/comment-list/comment-list';
import {NewCommentForm} from '@common/comments/new-comment-form';
import {DownloadPageTitleDetails} from '@app/download/download-page/download-page-title-details';
import {AnimatePresence, m} from 'framer-motion';
import {opacityAnimation} from '@ui/animation/opacity-animation';
import {useScrollToTop} from '@common/ui/navigation/use-scroll-to-top';
import {DownloadVideoPlayer} from '@app/download/download-video-player';
import {useSettings} from '@ui/settings/use-settings';
import {useAuth} from '@common/auth/use-auth';
import {useIsStreamingMode} from '@app/download/use-is-streaming-mode';

import {AdHost} from '@common/admin/ads/ad-host';
import {Episode} from '@app/titles/models/episode';
import {Title} from '@app/titles/models/title';
import {Video} from '@app/titles/models/video';
import {PageMetaTags} from '@common/http/page-meta-tags';
import {useLayoutEffect} from '@react-aria/utils';
import {VideoThumbnail} from '@app/download/video-thumbnail';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {Link, useParams} from 'react-router-dom';
import {Lien} from '@app/titles/models/lien';
import DynamicIframe from '@app/iframe/dynamic-iframe';
import Cookies from 'js-cookie';

import {UseDownloadPageResponse} from '@app/download/requests/use-download-page-video';
import {DownloadPageAlternativeVideos} from '@app/download/download-page/download-page-alternative-videos';
import {useForm} from 'react-hook-form';
import {MediaPlayIcon} from '@ui/icons/media/media-play';
import {MediaDownloadIcon} from '@ui/icons/media/media-download';
import {useDownloadGetTitle} from '@app/download/requests/use-download-get-title';
import {Skeleton} from '@ui/skeleton/skeleton';
import {SectionHelper} from '@common/ui/other/section-helper';
import {ErrorIcon} from '@ui/icons/material/Error';
import {setInLocalStorage} from '@ui/utils/hooks/local-storage';
import DetectBlockedDomain from '@app/download/download-page/block-detect';

export function DownloadPage() {
  const darkThemeVars = useDarkThemeVariables();
  useScrollToTop();
  return (
    <Fragment>
      <MainNavbar />
      <div style={darkThemeVars} className="dark min-h-screen bg text">
        <div className="container mx-auto p-14 md:p-24">
          <Content />
          <Footer className="mt-48" />
        </div>
      </div>
    </Fragment>
  );
}

function Content() {
  const {season} = useParams(); // Déclarez vos hooks dès le début, en utilisant des valeurs par défaut
  const [currentEpisode, setCurrentEpisode] = useState<
    Lien | Episode | undefined
  >(undefined);
  const [currentVideo, setCurrentVideo] = useState<Lien | Video | undefined>(
    undefined,
  );

  const seasonSelected = season
    ? parseInt(season)
    : (currentEpisode as Lien)?.saison ||
      (currentEpisode as Episode)?.season_number;
  // Appel du hook pour récupérer les données, il peut renvoyer undefined en début de chargement
  const query = useDownloadGetTitle(seasonSelected);

  const {titles, comments} = useSettings();
  const {isLoggedIn, hasPermission} = useAuth();

  useEffect(() => {
    if (query.data) {
      const data = query.data;
      setCurrentEpisode(data.video);
      setCurrentVideo(data.video);
    }
  }, [query.data]);
  const handleUpdateMedia = (media: Lien | Episode) => {
    const newMedia = {...media};
    setCurrentEpisode(newMedia);
    setCurrentVideo(newMedia as Lien);
  };

  const data = query.data;
  const title = data?.title;

  // Renvoyez un composant de chargement tant que les données ne sont pas prêtes
  if (query.isLoading || !data) {
    return <Skeleton className="max-w-120" />;
  }

  // Le reste de votre rendu utilise currentEpisode et currentVideo (qui sont mis à jour via useEffect)
  let commentable: Episode | Title | Video | Lien | undefined =
    currentVideo || data.video;

  commentable = data.title || title;

  const shouldShowComments =
    title &&
    (currentVideo || data.video) &&
    titles.enable_comments &&
    hasPermission('comments.view');
  const streamingOnly = Cookies.get('only_streaming') === '1';

  return (
    <>
      {(title?.type === 'movie' ||
        title?.type === 'animes' ||
        title?.type === 'series') &&
        currentVideo && (
          <div className="mx-auto w-full">
            <DetectBlockedDomain />
          </div>
        )}
      <PageMetaTags query={query} />
      {currentVideo && (
        <VideoWrapper data={data} video={currentVideo as Lien} />
      )}
      {currentVideo && (
        <DownloadPageAlternativeVideos
          data={data}
          updateMedia={handleUpdateMedia} // Passage de la fonction à VideoList
          currentVideo={currentVideo}
        />
      )}
      {/* Le reste de votre composant */}

      <AdHost slot="watch_top" className="pt-48" />
      <section className="mt-42 items-start gap-56 lg:flex">
        <div className="flex-auto">
          <DownloadPageTitleDetails data={data} />
          {title && !streamingOnly && hasPermission('liens.view') && (
            <DynamicIframe title={title} season={seasonSelected} />
          )}
          {title && streamingOnly && hasPermission('liens.view') && (
            <div className="mx-auto my-40 max-w-850 rounded border border-dashed px-20 py-30 text-center">
              <div className="mb-8 text-sm font-semibold text-warning">
                <Trans message="No download link because you have activated 'Streaming Only' mode, you can activate 'Streaming & Download' Mode by clicking below." />
              </div>
              <StreamingMode />
              <div className="text-base text-muted"></div>
            </div>
          )}
          {shouldShowComments && (
            <CommentList
              commentable={commentable!}
              canDeleteAllComments={
                isLoggedIn && hasPermission('comments.delete')
              }
              className="mt-44"
              perPage={20}
            >
              {isLoggedIn && hasPermission('comments.create') && (
                <NewCommentForm
                  commentable={commentable!}
                  className="mb-14 mt-24"
                />
              )}
            </CommentList>
          )}
        </div>
      </section>
    </>
  );
}

function StreamingMode() {
  const [streamingOnly, setStreamingOnly] = useState(
    Cookies.get('only_streaming') === '1',
  );
  const handleSwitchChange = (event: {target: {checked: boolean}}) => {
    const checked = event.target.checked;
    setStreamingOnly(checked);
    if (checked) {
      Cookies.set('only_streaming', '1');
    } else {
      Cookies.remove('only_streaming');
    }
    window.location.reload();
  };
  const methods = useForm({defaultValues: {streaming_only: streamingOnly}});
  useEffect(() => {
    methods.reset({streaming_only: streamingOnly});
  }, [streamingOnly, methods]);
  return (
    <Button
      variant="text"
      startIcon={streamingOnly ? <MediaPlayIcon /> : <MediaDownloadIcon />}
      onClick={() => {
        handleSwitchChange({target: {checked: !streamingOnly}});
      }}
    >
      {streamingOnly ? (
        <Trans message="Streaming only" />
      ) : (
        <Trans message="Streaming & Download" />
      )}
    </Button>
  );
}

interface VideoWrapperProps {
  data?: UseDownloadPageResponse;
  video: Lien;
}

function VideoWrapper({data, video}: VideoWrapperProps) {
  const isStreamingMode = useIsStreamingMode();
  const {hasPermission} = useAuth();
  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    setIsVisible(true);
  }, []);
  return (
    <AnimatePresence initial={false} mode="wait">
      {video.id && isVisible && (
        <m.div key="player" {...opacityAnimation}>
          {hasPermission('videos.play') && video && video?.streaming ? (
            <DownloadVideoPlayer
              title={data?.title}
              episode={data?.episode}
              video={video}
              logPlays
              showEpisodeSelector={isStreamingMode}
            />
          ) : (
            <ErrorMessage video={data?.video!} />
          )}
        </m.div>
      )}
    </AnimatePresence>
  );
}

interface UpgradeMessageProps {
  video: Lien;
}

function ErrorMessage({video}: UpgradeMessageProps) {
  return (
    <div className="relative flex aspect-video items-center justify-center bg-alt">
      <div className="blur">
        <VideoThumbnail video={video} />
      </div>
      <div className="absolute h-max w-max max-w-full rounded-lg bg-black/60 p-24 text-lg font-medium">
        <div>
          <Trans message="This video is not available for moment. Please try again later." />
        </div>
        {video.src && (
          <div className="mt-14 text-center">
            <Button
              variant="flat"
              color="primary"
              elementType={Link}
              to={`${video.src}`}
            >
              <Trans message="Download" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function UpgradeMessage({video}: UpgradeMessageProps) {
  return (
    <div className="relative flex aspect-video items-center justify-center bg-alt">
      <div className="blur">
        <VideoThumbnail video={video} />
      </div>
      <div className="absolute h-max w-max max-w-full rounded-lg bg-black/60 p-24 text-lg font-medium">
        <div>
          <Trans message="Your current plan does not allow watching videos. Upgrade to unlock this feature." />
        </div>
        <div className="mt-14 text-center">
          <Button
            variant="flat"
            color="primary"
            elementType={Link}
            to="/pricing"
          >
            <Trans message="Upgrade" />
          </Button>
        </div>
      </div>
    </div>
  );
}
